/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import dompurify from 'dompurify';
import urlJoin from 'proper-url-join';

import { MfePrefix } from '../../../ts/mfe';
import { contentHubBaseUrl, isWxoEnabled } from './config';
import { isCrn, parse as parseCrn } from './crn';

/**
 * Returns the absolute url of the given path within the application
 * including the protocol and host
 * @param {String} path the path within the application
 * @return {String} the absolute path
 */
export const absoluteUrl = (path?: string): string => {
  return urlJoin(`${window.location.protocol}//${window.location.host}`, path);
};

/**
 * Returns the querystring value with a given name.
 * IE11 does not support URLSearchParams and core-js does not polyfill it in v2.
 * @param {String} name the name of the parameter that you want to extract
 * @return {String} the value of the querystring parameter, or an empty string
 */
export const getUrlParameter = (name: string): string => {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null
    ? ''
    : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/**
 * Returns a dictionary object of key-value pairs from the given query string.
 * Needed as a simple substitute for URLSearchParams
 * Input "A=x&B=y" will return {A: 'x', B: 'y'}
 * @param {String} queryString to parse, leading '?' can be included or omitted
 * @return {Object} key-value pairs
 */
export const getUrlParamsAsDictionary = (
  queryString: string
): Record<string, string> => {
  const retVal = {};
  if (queryString && queryString !== '') {
    let query = queryString.startsWith('?')
      ? queryString.substring(1)
      : queryString;
    query = query.replace(/\+/g, ' '); //decodeURIComp doesn't handle + for ' '
    const pairs = query.split('&');
    pairs.forEach(pair => {
      const kvPair = pair.split('=');
      const key = decodeURIComponent(kvPair[0]);
      const val = kvPair[1] ? decodeURIComponent(kvPair[1]) : '';
      retVal[key] = val;
    });
  }
  return retVal;
};

/**
 * Custom url-encoder to encode url components equivalently to angular-ui-router
 * Added for backward compatibility
 * @param {String} val the value to encode
 * @return {String} the custom encoded value
 */
export const customEncoder = (val: string): string =>
  val != null
    ? val.toString().replace(/(~|\/)/g, function (m) {
        return { '~': '~~', '/': '~2F' }[m] || '';
      })
    : val;

/**
 * Custom url-decoder to decode components encoded by the customEncoder
 * Added for backward compatibility
 * @param {String} val The custom encoded value to decode
 * @return {String} the decoded value
 */
export const customDecoder = (val: string): string =>
  val != null
    ? val.toString().replace(/(~~|~2F)/g, function (m) {
        return { '~~': '~', '~2F': '/' }[m] || '';
      })
    : val;

/**
 * Get the path portion of a url.
 * Used because IE11 doesn't support the URL api.
 * @param {String} urlString the url, either relative or absolute
 * @return {String} The path
 */
export const getPath = (urlString: string): string => {
  const parser = document.createElement('a');
  parser.href = dompurify.sanitize(urlString);
  return parser.pathname.replace(/\/+$/, '');
};

/**
 * Decode a path by passing each segment of it to decodeURIComponent, then reassembling it
 * Any forward-slashes in the decoded segments are
 * re-encoded so that they aren't mistaken for path segments
 *
 * Note that this is equivalent to the way that AngularJS decodes paths before normalizing in the $location service
 *
 * @param {String} path the path to decode
 * @return {String} the decoded path
 */
export const decodePath = (path: string): string => {
  const segments = path.split('/');
  let i = segments.length;

  while (i--) {
    segments[i] = decodeURIComponent(segments[i]);
    // encode forward slashes to prevent them from being mistaken for path separators
    segments[i] = segments[i].replace(/\//g, '%2F');
  }

  return segments.join('/');
};

/**
 *
 * URI Encoding function from AngularJS. Used to normalize urls when the UI loads, as some components depend on it
 *
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query         = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 * @param {String} val The value to encode
 * @param {Boolean}pctEncodeSpaces should spaces be encoded
 * @returns {String} the encoded string
 */
const encodeUriQuery = (val: string, pctEncodeSpaces: string): string =>
  encodeURIComponent(val)
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%3B/gi, ';')
    .replace(/%20/g, pctEncodeSpaces ? '%20' : '+');

/**
 *
 * URI Encoding function from AngularJS. Used to normalize urls when the UI loads, as some components depend on it
 *
 * We need our custom method because encodeURIComponent is too aggressive and doesn't follow
 * http://www.ietf.org/rfc/rfc3986.txt with regards to the character set (pchar) allowed in path
 * segments:
 *    segment       = *pchar
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 * @param {String} val The value to encode
 * @returns {String} the encoded string
 */
const encodeUriSegment = (val: string): string =>
  encodeUriQuery(val, 'true')
    .replace(/%26/gi, '&')
    .replace(/%3D/gi, '=')
    .replace(/%2B/gi, '+');

/**
 * Encode path using AngularJS normalized encoding rules
 *
 * @param {string} path Path to encode
 * @returns {string} the encoded path string
 */
export const encodePath = (path: string): string => {
  const segments = path.split('/');
  let i = segments.length;

  while (i--) {
    // decode forward slashes to prevent them from being double encoded
    segments[i] = encodeUriSegment(segments[i].replace(/%2F/g, '/'));
  }

  return segments.join('/');
};

/**
 * Encode path using `encodeURIComponent`
 *
 * @param {string} path Path to encode
 * @returns {string} the encoded path string
 */
export const encodePathWithURIComponent = (path: string): string => {
  const segments = path.split('/');
  let i = segments.length;

  while (i--) {
    // decode forward slashes to prevent them from being double encoded
    segments[i] = encodeURIComponent(segments[i].replace(/%2F/g, '/'));
  }

  return segments.join('/');
};

/**
 * Normalize an encoded path using Web standards after decoding an Angular Js url.
 * This is called by util/history when the app is initialized.
 *
 * @param {String} path the path to encode
 * @returns {String} the normalized path
 */
export const normalizePath = (path: string): string =>
  encodePathWithURIComponent(decodePath(path));

interface Tenant {
  region: string;
  instance: string;
}

/**
 * Extract the tenant (region and instance) information from the page path.
 *
 * @param path The current page path.
 */
export const extractTenant = (path: string): Tenant | null => {
  const basePath = getPath(document.baseURI);
  // Split around the '/' chars ignoring the leading '/'
  const elements = path
    .substr(path.indexOf(basePath) + basePath.length)
    .split('/')
    .filter(value => value);
  const instance = elements.shift();
  if (instance && isCrn(instance)) {
    const decodedInstance = decodeURIComponent(instance);
    const region = parseCrn(decodedInstance).region;
    return {
      region,
      instance: decodedInstance
    };
  }
  return null;
};

/**
 * Get the content hub site url
 *
 * @param {string} parentPageName the parent page name indicates where the site is launched from
 * @param {string} accountId the account id
 * @param {string} instanceId the service instance id
 * @returns {string} the content hub site url
 */
export const getContentHubUrl = (
  parentPageName: string,
  accountId: string,
  instanceId: string
): string =>
  `${contentHubBaseUrl}/?parentPageName=${parentPageName}&accountId=${accountId}&instanceId=${instanceId}`;

/**
 * Get the base URI for the server; it should be used whenever we need to form
 * a URL used to directly request an asset from the server, or when we need to
 * generate the URL for an API to be executed on the server by a external
 * third party (e.g. the docs endpoint, or OAuth extension endpoint).
 *
 * This function returns the base URI (when in Cloud) or the server origin
 * and the MFE prefix (when operating as an MFE within watsonx Orchestrate).
 *
 * @returns The base URI to directly access the server
 */
export const getServerBaseUri = (): string =>
  isWxoEnabled
    ? urlJoin(window.location.origin, MfePrefix.ASSISTANTS)
    : document.baseURI;

/**
 * Used for URLs where we need the MFE prefix to be prepended if we are
 * operating as an MFE under WXO.
 *
 * @param url The URL path we may want to prepend the MFE prefix.
 * @returns The passed-in URL, prepended with the MFE prefix, if appropriate
 */
export const prependMfePrefixIfNeeded = (url: string): string =>
  urlJoin(isWxoEnabled ? MfePrefix.ASSISTANTS : '', url);

// HTTP URL validation regex used by store (backend) to ensure URLs are valid in tooling.
export const HTTP_URL_REGEX =
  "^https?:\\/\\/[a-zA-Z0-9-._~:\\/?#\\[\\]@!$&'()*\\+,;=%]+$";

// GRPC URL validation regex used by store (backend) to ensure URLs are valid in tooling.
export const GRPC_URL_REGEX = "^[a-zA-Z0-9-._~:\\/?#\\[\\]@!$&'()*\\+,;=%]+$";
